<template>
  <Box>
    <div
      v-if="hasMembershipSavings || hasPricingTierSavings"
      class="p-4 border-b border-b-gray-100 bg-gray-50 space-y-3"
    >
      <div v-if="hasMembershipSavings" class="flex space-x-2">
        <div class="flex-shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5 text-green-600"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <div class="text-sm">
          Saving
          <strong>{{ cart.attributes.membership_savings.format }}</strong>
          with
          <strong>{{ membershipTier?.attributes?.tier_name }}</strong>
          discounts
        </div>
      </div>
      <div v-if="hasPricingTierSavings" class="flex space-x-2">
        <div class="flex-shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5 text-green-600"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <div class="text-sm">
          Saving
          <strong>{{ cart.attributes.pricing_tier_savings.format }}</strong>
          with
          <i>{{ pricingTier?.attributes?.title }}</i>
        </div>
      </div>
    </div>
    <div v-if="supportsSelectionScreen && !hasBeenPaid">
      <SecondaryButton @click="goToCart()" class="ml-3 mt-3 rounded-full" :size="'md'">
        <template #left-icon>
          <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
        </template>
        Edit Cart
      </SecondaryButton>
    </div>
    <CartItems :cart-items="list" :bottle="bottle" />
    <DiscountCodeModule
      v-if="hasDiscountCodeBox"
      @discount-updated="updateDiscount"
      @discount-removed="removeDiscount"
    />
    <div v-if="checkoutAttributes" class="border-t border-gray-200">
      <div class="py-4 px-4">
        <div class="flex justify-between items-center pb-2">
          <span class="text-xs font-bold">Cart Total</span>
          <span class="text-xs font-medium">
            {{ checkoutAttributes.cart_total_amount.format }}
          </span>
        </div>
        <div v-if="hasFulfillmentAmountOff" class="flex justify-between items-center pb-2">
          <span class="text-xs font-bold">Fulfillment Discount</span>
          <span class="text-xs font-medium">({{ checkoutAttributes.order_fulfillment_discount_amount.format }})</span>
        </div>
        <div v-if="hasFulfillmentAmount" class="flex justify-between items-center pb-2">
          <span class="text-xs font-bold">Fulfillment</span>
          <span v-if="hasFufillmentAmountOff" class="text-xs font-medium">
            <s>{{ checkoutAttributes.fulfillment_amount.format }}</s>
            {{ checkoutAttributes.net_fulfillment_amount.format }}
          </span>
          <span v-else class="text-xs font-medium">
            {{ checkoutAttributes.fulfillment_amount.format }}
          </span>
        </div>
        <div v-if="hasDiscountCodeAmount" class="flex justify-between items-center pb-2">
          <span class="text-xs font-bold">Discount Codes</span>
          <span class="text-xs font-medium">({{ checkoutAttributes.discount_code_amount.format }})</span>
        </div>
        <div v-if="hasDiscountCodeAmount" class="flex justify-between items-center pb-2">
          <span class="text-xs font-medium">{{ checkoutAttributes.discount_codes }}</span>
        </div>
        <div class="flex justify-between items-center pb-2">
          <span class="text-xs font-bold">Subtotal</span>
          <span class="text-xs font-medium">
            {{ checkoutAttributes.bottle_subtotal.format }}
          </span>
        </div>
        <div v-if="hasFeeToChargeCustomer" class="pb-2">
          <div class="flex justify-between items-center">
            <span class="text-xs font-bold">Taxes &amp; Fees</span>
            <span class="text-xs font-medium">
              {{ checkoutAttributes.taxes_and_fees?.format }}
            </span>
          </div>
          <div v-if="showTaxesAndFeesBreakdown">
            <div
              v-for="fee in checkoutAttributes.taxes_and_fees_breakdown"
              :key="fee.description"
              class="flex justify-between items-center"
            >
              <span class="text-xs text-gray-500">{{ fee.description }}</span>
              <span class="text-xs text-gray-500">
                {{ fee.amount?.format }}
              </span>
            </div>
          </div>
          <div v-else class="-mt-1">
            <button class="text-xs text-gray-500" @click="showTaxesAndFeesBreakdown = true">View breakdown</button>
          </div>
          <div v-if="(hasMembershipSavings || hasPricingTierSavings) && totalSavingsFormatted" class="text-center mt-2">
            <span class="text-xs text-gray-500">
              You saved
              <strong>{{ totalSavingsFormatted }}</strong>
              today!
            </span>
          </div>
        </div>
        <div v-else-if="hasTaxAmount" class="pb-2">
          <div class="flex justify-between items-center">
            <span class="text-xs font-bold">Taxes</span>
            <span class="text-xs font-medium">
              {{ checkoutAttributes.tax_amount?.format }}
            </span>
          </div>
          <div v-if="showTaxesAndFeesBreakdown">
            <div
              v-for="fee in checkoutAttributes.taxes_and_fees_breakdown"
              :key="fee.description"
              class="flex justify-between items-center"
            >
              <span class="text-xs text-gray-500">{{ fee.description }}</span>
              <span class="text-xs text-gray-500">
                {{ fee.amount?.format }}
              </span>
            </div>
          </div>
          <div v-else class="-mt-1">
            <button class="text-xs text-gray-500" @click="showTaxesAndFeesBreakdown = true">View breakdown</button>
          </div>
          <div v-if="(hasMembershipSavings || hasPricingTierSavings) && totalSavingsFormatted" class="text-center mt-2">
            <span class="text-xs text-gray-500">
              You saved
              <strong>{{ totalSavingsFormatted }}</strong>
              today!
            </span>
          </div>
        </div>
        <div v-if="hasTipAmount" class="flex justify-between items-center pb-2">
          <span class="text-xs font-bold">Tip</span>
          <span class="text-xs font-medium">
            {{ checkoutAttributes.tip_amount.format }}
          </span>
        </div>
      </div>
      <div class="border-t border-gray-200 py-4 px-4">
        <div class="flex justify-between items-center">
          <span class="text-md font-bold">Total</span>
          <span class="text-xs font-medium">
            {{ checkoutAttributes.bottle_total.format }}
          </span>
        </div>

        <div
          v-if="hasImmediatePassRewardAmount && !hasBeenPaid"
          class="flex justify-between items-center pb-2"
          :class="{ 'pt-2': true }"
        >
          <span class="text-xs font-bold flex items-center">
            Amount covered by sign up bonus
            <svg class="ml-1" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5.47566 3.10655C5.78752 2.2532 5.94344 1.82653 6.09146 1.62522C6.79079 0.674113 8.21193 0.674113 8.91126 1.62522C9.05927 1.82653 9.2152 2.2532 9.52705 3.10655L9.56828 3.21936C9.63564 3.4037 9.66933 3.49586 9.70742 3.57682C10.0099 4.21975 10.6308 4.6539 11.3385 4.71735C11.4276 4.72534 11.5258 4.72534 11.722 4.72534H12.0599C12.4527 4.72534 12.6491 4.72534 12.7453 4.73601C14.266 4.90469 14.8532 6.80616 13.6924 7.80298C13.619 7.86602 13.4569 7.97676 13.1325 8.19824C13.0264 8.27065 12.9734 8.30686 12.9279 8.34193C12.2703 8.84864 11.9941 9.70888 12.2338 10.5037C12.2504 10.5587 12.274 10.6232 12.3211 10.7522C12.5128 11.2766 12.6086 11.5389 12.635 11.6733C12.9082 13.063 11.5035 14.1846 10.2087 13.6107C10.0834 13.5552 9.86191 13.412 9.41884 13.1258L8.86069 12.7653C8.59668 12.5947 8.46467 12.5095 8.33268 12.4492C7.80473 12.2079 7.19799 12.2079 6.67004 12.4492C6.53804 12.5095 6.40604 12.5947 6.14203 12.7653L5.59967 13.1156C5.14386 13.4101 4.91596 13.5573 4.7868 13.6139C3.49604 14.1791 2.10065 13.0649 2.36618 11.681C2.39275 11.5426 2.49131 11.2729 2.68842 10.7335C2.73617 10.6028 2.76004 10.5375 2.77685 10.4816C3.0126 9.69736 2.74642 8.84862 2.10503 8.33944C2.05931 8.30315 2.00607 8.26573 1.8996 8.1909C1.5765 7.96382 1.41496 7.85028 1.34103 7.78473C0.207542 6.77966 0.799813 4.90697 2.3051 4.73646C2.40327 4.72534 2.60073 4.72534 2.99564 4.72534H3.28718C3.47722 4.72534 3.57224 4.72534 3.65856 4.71785C4.37046 4.65605 4.99531 4.21914 5.2977 3.5717C5.33436 3.4932 5.36698 3.40395 5.43221 3.22545L5.47566 3.10655Z"
                fill="#94E86C"
              />
            </svg>
          </span>
          <span class="text-xs font-medium">({{ checkoutAttributes.pass_signup_credit_amount.format }})</span>
        </div>
        <div
          v-if="hasPassCreditAmount && !hasBeenPaid"
          class="flex justify-between items-center pb-2"
          :class="{ 'pt-2': !hasImmediatePassRewardAmount }"
        >
          <span class="text-xs font-bold flex items-center">
            Amount covered by pass credit
            <svg class="ml-1" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5.47566 3.10655C5.78752 2.2532 5.94344 1.82653 6.09146 1.62522C6.79079 0.674113 8.21193 0.674113 8.91126 1.62522C9.05927 1.82653 9.2152 2.2532 9.52705 3.10655L9.56828 3.21936C9.63564 3.4037 9.66933 3.49586 9.70742 3.57682C10.0099 4.21975 10.6308 4.6539 11.3385 4.71735C11.4276 4.72534 11.5258 4.72534 11.722 4.72534H12.0599C12.4527 4.72534 12.6491 4.72534 12.7453 4.73601C14.266 4.90469 14.8532 6.80616 13.6924 7.80298C13.619 7.86602 13.4569 7.97676 13.1325 8.19824C13.0264 8.27065 12.9734 8.30686 12.9279 8.34193C12.2703 8.84864 11.9941 9.70888 12.2338 10.5037C12.2504 10.5587 12.274 10.6232 12.3211 10.7522C12.5128 11.2766 12.6086 11.5389 12.635 11.6733C12.9082 13.063 11.5035 14.1846 10.2087 13.6107C10.0834 13.5552 9.86191 13.412 9.41884 13.1258L8.86069 12.7653C8.59668 12.5947 8.46467 12.5095 8.33268 12.4492C7.80473 12.2079 7.19799 12.2079 6.67004 12.4492C6.53804 12.5095 6.40604 12.5947 6.14203 12.7653L5.59967 13.1156C5.14386 13.4101 4.91596 13.5573 4.7868 13.6139C3.49604 14.1791 2.10065 13.0649 2.36618 11.681C2.39275 11.5426 2.49131 11.2729 2.68842 10.7335C2.73617 10.6028 2.76004 10.5375 2.77685 10.4816C3.0126 9.69736 2.74642 8.84862 2.10503 8.33944C2.05931 8.30315 2.00607 8.26573 1.8996 8.1909C1.5765 7.96382 1.41496 7.85028 1.34103 7.78473C0.207542 6.77966 0.799813 4.90697 2.3051 4.73646C2.40327 4.72534 2.60073 4.72534 2.99564 4.72534H3.28718C3.47722 4.72534 3.57224 4.72534 3.65856 4.71785C4.37046 4.65605 4.99531 4.21914 5.2977 3.5717C5.33436 3.4932 5.36698 3.40395 5.43221 3.22545L5.47566 3.10655Z"
                fill="#94E86C"
              />
            </svg>
          </span>
          <span class="text-xs font-medium">({{ checkoutAttributes.pass_credit_amount.format }})</span>
        </div>
        <div
          v-if="hasAccountCreditAmount && !hasBeenPaid"
          class="flex justify-between items-center pb-2"
          :class="{ 'pt-2': !hasPassCreditAmount && !hasImmediatePassRewardAmount }"
        >
          <span class="text-xs font-bold">Amount covered by account credit</span>
          <span class="text-xs font-medium">({{ checkoutAttributes.account_credit_amount.format }})</span>
        </div>
        <div
          v-if="(hasAccountCreditAmount || hasPassCreditAmount) && !hasBeenPaid"
          class="flex justify-between items-center pb-2"
        >
          <span class="text-xs font-bold">Amount remaining to charge</span>
          <span class="text-xs font-medium">{{ checkoutAttributes.chargeable_amount.format }}</span>
        </div>
      </div>
    </div>
  </Box>
</template>
<script>
import DiscountCodeModule from '@/components/Checkout/DiscountCodeModule'
import Box from '@/components/Molecules/Box'
import SecondaryButton from '../Molecules/SecondaryButton.vue'
import CartItems from '@/components/Checkout/CartItems'

export default {
  components: {
    Box,
    DiscountCodeModule,
    SecondaryButton,
    CartItems
  },
  props: {
    list: {
      type: Array,
      required: true
    },
    checkoutAttributes: {
      type: Object,
      required: false,
      default: null
    },
    useNeutralTheme: {
      type: Boolean,
      required: false,
      default: false
    },
    hasDiscountCodeBox: {
      type: Boolean,
      required: false,
      default: false
    },
    hasDiscountCodeList: {
      type: Boolean,
      required: false,
      default: false
    },
    passedBottle: {
      type: Object,
      required: false,
      default: null
    }
  },
  data: () => ({ showTaxesAndFeesBreakdown: false }),
  computed: {
    checkout() {
      return this.$store.getters.checkout
    },
    bottle() {
      return this.passedBottle || this.$store.getters.currentBottle
    },
    cart() {
      return this.$store.getters.currentCart
    },
    hasMembershipSavings() {
      return this.cart?.attributes?.membership_savings?.cents > 0
    },
    hasPricingTierSavings() {
      return this.cart?.attributes?.pricing_tier_savings?.cents > 0
    },
    membershipTier() {
      return this.$store.getters.membershipTier
    },
    pricingTier() {
      return this.$store.getters.pricingTierWithID(this.cart?.relationships?.pricing_tier?.data?.id)
    },
    unavailableItems() {
      return this.bottle?.relationships?.removed_category_product_store_pairs_still_in_cart?.data
    },
    hasFulfillmentAmount() {
      return this.checkout?.attributes.fulfillment_amount && this.checkout?.attributes.fulfillment_amount.cents > 0
    },
    hasFufillmentAmountOff() {
      return (
        this.checkout?.attributes.fulfillment_amount_off && this.checkout?.attributes.fulfillment_amount_off.cents > 0
      )
    },
    hasDiscountCodeAmount() {
      return this.checkout?.attributes.discount_code_amount && this.checkout?.attributes.discount_code_amount.cents > 0
    },
    hasFulfillmentAmountOff() {
      return (
        this.checkout?.attributes.order_fulfillment_discount_amount &&
        this.checkout?.attributes.order_fulfillment_discount_amount.cents > 0
      )
    },
    hasAccountCreditAmount() {
      return (
        this.checkout?.attributes.account_credit_amount && this.checkout?.attributes.account_credit_amount.cents > 0
      )
    },
    hasImmediatePassRewardAmount() {
      return (
        this.checkout?.attributes.pass_signup_credit_amount &&
        this.checkout?.attributes.pass_signup_credit_amount.cents > 0
      )
    },
    hasPassCreditAmount() {
      return this.checkout?.attributes.pass_credit_amount && this.checkout?.attributes.pass_credit_amount.cents > 0
    },
    hasTaxAmount() {
      return this.checkout?.attributes.tax_amount && this.checkout?.attributes.tax_amount.cents > 0
    },
    hasTipAmount() {
      return this.checkout?.attributes.tip_amount && this.checkout?.attributes.tip_amount.cents > 0
    },
    hasFeeToChargeCustomer() {
      return (
        this.checkout?.attributes.fee_to_charge_customer && this.checkout?.attributes.fee_to_charge_customer.cents > 0
      )
    },
    hasBeenPaid() {
      return this.checkout?.attributes.payment_status == 'paid'
    },
    supportsSelectionScreen() {
      return this.bottle?.attributes?.supports_selection_screen
    },
    totalSavingsAmount() {
      const membershipSavings = this.cart?.attributes?.membership_savings?.cents || 0
      const pricingTierSavings = this.cart?.attributes?.pricing_tier_savings?.cents || 0
      return membershipSavings + pricingTierSavings
    },
    totalSavingsFormatted() {
      if (this.totalSavingsAmount > 0) {
        const membershipSavingsFormatted = this.cart?.attributes?.membership_savings?.format || ''
        const pricingTierSavingsFormatted = this.cart?.attributes?.pricing_tier_savings?.format || ''

        if (this.hasMembershipSavings && this.hasPricingTierSavings) {
          const membershipAmount = membershipSavingsFormatted.replace(/[^\d.,]/g, '')
          const pricingTierAmount = pricingTierSavingsFormatted.replace(/[^\d.,]/g, '')
          const currencySymbol = membershipSavingsFormatted.replace(/[\d.,]/g, '')[0] || '$'

          const totalAmount =
            parseFloat(membershipAmount.replace(',', '')) + parseFloat(pricingTierAmount.replace(',', ''))
          return `${currencySymbol}${totalAmount.toFixed(2)}`
        } else if (this.hasMembershipSavings) {
          return membershipSavingsFormatted
        } else if (this.hasPricingTierSavings) {
          return pricingTierSavingsFormatted
        }
      }
      return null
    }
  },
  methods: {
    updateDiscount(data) {
      this.$store.dispatch('addDiscountCode', data)
    },
    removeDiscount(data) {
      this.$store.dispatch('removeAppliedDiscountCode', data)
    },
    goToCart() {
      this.$router.push({ name: 'Selection', params: { bottleid: this.bottle?.id }, query: { showCart: 'y' } })
    }
  }
}
</script>
